<template>
  <resource-list-wrapper
    :class="[listPage && !totalItems && !noData && !loading ? 'wrapper-move-top' : '']"
    :hidden="listPage && !totalItems && !noData && !loading"
    :title="$t('resources.authors.authors')"
    :sub-title="$t('resources.authors.sub_title')">
    <template #actions>
      <v-btn v-if="totalItems || noData || loading" color="primary" class="left-icon" to="/authors/create">
        <plus-icon class="mr-2"></plus-icon>
        {{ $t("generic.buttons.create") }}
      </v-btn>
    </template>

    <template #default>
      <delete-resource-modal :resource-name="resourceName"></delete-resource-modal>
      <va-list
        ref="list"
        :filter.sync="customFiltersValues"
        :filters="filters"
        disable-export
        disable-create
        @update:selected="getBulkSelectedItems"
        @no-data="handleNoData"
        @update:total="handleTotalItems"
        @update:loading="handleLoading"
        :create-button-label="$t('generic.buttons.create')">
        <template #no-data>
          <empty-list
            :list-page="listPage"
            :title="$t('resources.authors.list_page_empty_list_title')"
            :light-image="emptyLightImg"
            :dark-image="emptyDarkImg"
            :route="{ name: 'authors_create' }">
            <template #subtitle>
              {{ $t("resources.authors.list_page_empty_list_subtitle_1") }}
              <a href="https://help.siterunner.com/help/author-what-is-an-author " target="_blank" class="help-link">{{
                $t("resources.authors.list_page_empty_list_subtitle_link")
              }}</a>
              {{ $t("resources.authors.list_page_empty_list_subtitle_2") }}
            </template>
          </empty-list>
        </template>
        <template #no-results>
          <div class="no-results">
            {{ $t("custom.no_data") }}
          </div>
        </template>
        <template v-slot:[`bulk.actions`]>
          <v-btn
            @click="editResource('authors', bulkSelectedItems[0].id)"
            v-if="bulkSelectedItems.length < 2"
            text
            color="primary">
            <v-icon small>mdi-pencil-outline</v-icon>
            <span class="ml-2">{{ $t("generic.buttons.edit") }}</span>
          </v-btn>
        </template>
        <template #[`filter.q`]="props">
          <v-text-field
            class="search-filter"
            hide-details
            v-bind="props"
            label=""
            :placeholder="$t('generic.search_label')"
            @input="val => updateFiltersValues('q', val)">
          </v-text-field>
        </template>
        <va-data-table :disable-clone="true" :fields="fields" class="va-table--selectable" @row-click="handleRowClick">
          <template v-slot:[`field.photo`]="{ value }">
            <img v-if="value" class="logo-img" :src="getImageUrl(value)" alt="author photo" />
          </template>
          <template #[`field.name`]="{ item }">
            <div class="no-style text--primary cell-ellipsis">
              <span>{{ item.name }}</span>
            </div>
          </template>
          <template #[`field.updatedAt`]="{ item }">
            {{ changeDateFormat(item.updatedAt, isUserUS($store.state.user.timezone)) }}
          </template>
          <template #[`cell.actions`]="{ item }">
            <DataListActions
              :actions="[
                { title: 'Edit', icon: 'mdi-pencil-outline', function: editResource.bind(this, 'authors', item.id) },
                { title: 'Delete', icon: 'mdi-trash-can-outline', function: deleteAuthor.bind(this, item) },
              ]"></DataListActions>
          </template>
        </va-data-table>
      </va-list>
    </template>
  </resource-list-wrapper>
</template>

<script>
  import { changeDateFormat } from "@/@core/utils/filter";
  import { isUserUS } from "@/utility/isUserUS";
  import ResourceListWrapper from "@/components/resourceWrappers/ResourceListWrapper";
  import PlusIcon from "@/components/icons/PlusIcon";
  import DataListActions from "@/components/custom/DataListActions";
  import EmptyList from "@/components/custom/EmptyList";
  import DeleteResourceModal from "@/components/resources/DeleteResourceModal";
  import RowClick from "@/mixins/rowClickMixin";
  // Images
  import emptyLightImg from "@/assets/images/empty-list/author-empty-list-light.svg";
  import emptyDarkImg from "@/assets/images/empty-list/author-empty-list-dark.svg";
  import debounce from "lodash.debounce";
  import definedAbility from "@/ability";

  export default {
    components: {
      ResourceListWrapper,
      PlusIcon,
      DataListActions,
      EmptyList,
      DeleteResourceModal,
    },
    mixins: [RowClick],
    props: {
      resourceName: {
        type: String,
        default() {
          return this.$route.meta.resource;
        },
      },
      title: {
        type: String,
        required: true,
      },
      /**
       * True if component is used in VA-admin generated route for resource list;
       * False when used elsewhere (Single domain view for example).
       */
      listPage: {
        type: Boolean,
        default: true,
      },
      domainId: {
        type: String,
        default: "",
      },
    },
    data() {
      return {
        ability: definedAbility(),
        emptyLightImg,
        emptyDarkImg,
        changeDateFormat,
        isUserUS,
        noData: false,
        loading: true,
        totalItems: 0,
        customFiltersValues: {},
        filters: [
          {
            source: "q",
            alwaysOn: true,
          },
        ],
        fields: [
          {
            source: "photo",
            width: "74px",
          },
          {
            source: "name",
            sortable: true,
            width: "50%",
          },
          {
            source: "updatedAt",
            sortable: true,
            width: "50%",
          },
        ],
        imageUrl: process.env.VUE_APP_API,
        bulkSelectedItems: [],
      };
    },
    methods: {
      handleNoData(val) {
        this.noData = val;
      },
      handleLoading(val) {
        this.loading = val;
      },
      handleTotalItems(val) {
        this.totalItems = val;
      },
      getBulkSelectedItems(val) {
        this.bulkSelectedItems = val;
      },
      async deleteAuthor(item) {
        this.$store.dispatch(`${this.resourceName}/displayDeleteModal`, {
          displayDeleteModal: true,
          items: [item],
        });
      },
      editResource(resource, resourceId) {
        this.$router.push({ name: `${resource}_edit`, params: { id: resourceId } });
      },
      getImageUrl(value) {
        if (this.ability.can("upload", "media object to inulti server")) {
          return this.imageUrl + value.contentUrl;
        } else {
          return value.contentUrl;
        }
      },
      updateFiltersValues: debounce(function (source, value) {
        this.customFiltersValues = {
          ...this.customFiltersValues,
          [source]: value,
        };
      }, 400),
    },
  };
</script>

<style lang="scss" scoped>
  .logo-img {
    max-width: 36px;
    min-height: 36px;
    margin: 5px 0;
    border-radius: 50%;
  }
</style>
